import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { EDIT_SUBTASK_ERROR } from "~/store/action_types";
import { editSubtask } from "~/store/actions/actions.subtasks.jsx";
import { getIsSubtaskCompleted, getSubtaskById } from "~/store/selectors/selector.subtasks.jsx";
import SubtaskItem from "./SubtaskItem.jsx";
import { showErrorToast } from "../Toast/index.js";

const SubtaskItemContainer = ({ id, ...rest }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const subtask = useSelector((state) => getSubtaskById(state, id));
  const isSubtaskCompleted = useSelector((state) => getIsSubtaskCompleted(state, id));
  const trashed = subtask?.is_trashed;
  const projectId = subtask?.project_id;
  const taskId = subtask?.task_id;

  const [state, setState] = useState(() => ({
    assignee_id: subtask.assignee_id,
    body: subtask.name,
    due_on: subtask.due_on,
  }));

  useEffect(() => {
    if (subtask && !subtask.pending) {
      setState({
        assignee_id: subtask.assignee_id,
        body: subtask.name,
      });
    }
  }, [subtask]);

  const resolveSubTaskChanged = useCallback(
    (attributes) => {
      const { assignee_id, body, due_on } = attributes;
      const { assignee_id: stateAssigneeId, body: stateBody, due_on: stateDueOn } = state;
      const isAssigneeChanged = +assignee_id !== stateAssigneeId;
      const dateChanged = due_on !== stateDueOn;

      return (
        isAssigneeChanged ||
        dateChanged ||
        (Object.keys(attributes).length > 1 && (body !== stateBody || isAssigneeChanged))
      );
    },
    [state]
  );

  const handleChange = useCallback(
    (attributes) => {
      if (resolveSubTaskChanged(attributes)) {
        setState((prevState) => ({
          ...prevState,
          ...attributes,
        }));
        dispatch(editSubtask(projectId, taskId, id, attributes)).then((response) => {
          if (response.type === EDIT_SUBTASK_ERROR) {
            showErrorToast(`${t("Failed to update subtask")}.`);
          }
        });
      }
    },
    [resolveSubTaskChanged, dispatch, projectId, taskId, id, t]
  );

  return subtask && !isSubtaskCompleted && !trashed ? (
    <SubtaskItem
      {...rest}
      id={id}
      name={state.body}
      assigneeId={state.assignee_id}
      fakeAssigneeName={subtask.fake_assignee_name}
      projectId={projectId}
      taskId={taskId}
      isCompleted={subtask.is_completed}
      trashed={trashed}
      pending={subtask.pending}
      onChange={handleChange}
      dueOn={subtask.due_on}
      loadingDueDateReschedule={subtask?.loading_due_date_reschedule}
    />
  ) : null;
};

SubtaskItemContainer.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
SubtaskItemContainer.displayName = "SubtaskItemContainer";

export default SubtaskItemContainer;
